interface FloorAreaAssumptionBase {
  reason: string
  valueSqm: number
  unit: 'm²'
  sampleCount: number
  medianSqm: number
  lowerQuartileSqm: number
  upperQuartileSqm: number
  propertyType: string | null
  bedrooms: number | null
  selectionId?: string | null
  sourceVersion?: string | null
  contributors?: string[]
}

export type FloorAreaAssumption = FloorAreaAssumptionBase & (
  | { basis: 'local_advertised_area_median'; radiusMiles: number; geography?: string }
  | { basis: 'national_advertised_area_median'; radiusMiles: null; geography: 'United Kingdom'; corpusDigest: string; releaseVersion: 'national-floor-area-medians-v1' }
)

/** Validate the calculation evidence without turning a generic median into a measurement. */
export function parseFloorAreaAssumption(value: unknown, effectiveSqm: number | null, fail: () => never, numericValue: (value: unknown) => unknown = value => value): FloorAreaAssumption {
  const evidence = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : fail()
  const positive = (value: unknown): number => {
    const numeric = numericValue(value)
    return typeof numeric === 'number' && Number.isFinite(numeric) && numeric > 0 ? numeric : fail()
  }
  const numeric = { valueSqm: positive(evidence.valueSqm), sampleCount: positive(evidence.sampleCount), medianSqm: positive(evidence.medianSqm), lowerQuartileSqm: positive(evidence.lowerQuartileSqm), upperQuartileSqm: positive(evidence.upperQuartileSqm) }
  if (effectiveSqm !== numeric.valueSqm || evidence.unit !== 'm²' || typeof evidence.reason !== 'string' || !evidence.reason.trim()
    || !Number.isSafeInteger(numeric.sampleCount) || numeric.sampleCount < 10
    || numeric.lowerQuartileSqm > numeric.medianSqm || numeric.medianSqm > numeric.upperQuartileSqm) return fail()
  const bedrooms = evidence.bedrooms == null ? null : numericValue(evidence.bedrooms)
  if (bedrooms !== null && (typeof bedrooms !== 'number' || !Number.isSafeInteger(bedrooms) || bedrooms < 0)
    || evidence.propertyType != null && typeof evidence.propertyType !== 'string') return fail()
  let radiusMiles: number | null
  if (evidence.basis === 'local_advertised_area_median') radiusMiles = positive(evidence.radiusMiles)
  else if (evidence.basis === 'national_advertised_area_median') {
    if (evidence.radiusMiles !== null || evidence.geography !== 'United Kingdom'
      || typeof evidence.propertyType !== 'string' || !evidence.propertyType.trim() || bedrooms === null
      || evidence.releaseVersion !== 'national-floor-area-medians-v1'
      || typeof evidence.corpusDigest !== 'string' || !/^[a-f0-9]{64}$/.test(evidence.corpusDigest)
      || evidence.sourceVersion !== evidence.corpusDigest
      || evidence.selectionId !== `${evidence.releaseVersion}:${evidence.corpusDigest}:${evidence.propertyType}:${bedrooms}`
      || !Array.isArray(evidence.contributors) || evidence.contributors.length !== 0
      || numeric.valueSqm !== numeric.medianSqm) return fail()
    radiusMiles = null
  } else return fail()
  return { ...evidence, ...numeric, radiusMiles, bedrooms, propertyType: evidence.propertyType ?? null } as FloorAreaAssumption
}

export function isAssumedFloorAreaSource(source: string | null | undefined): boolean {
  return source === 'propertyMarket.assumed_local_median' || source === 'calculation.assumed_national_median'
}

export function floorAreaAssumptionSummary(assumption: FloorAreaAssumption, numberText: (value: number) => string): string {
  const scope = assumption.basis === 'national_advertised_area_median'
    ? `${numberText(assumption.sampleCount)} UK homes matching ${assumption.bedrooms === 0 && assumption.propertyType === 'studio' ? 'a studio layout' : `${assumption.bedrooms} bedrooms`} and property type ${assumption.propertyType?.replace(/_/g, ' ')}`
    : `${numberText(assumption.sampleCount)} similar homes within ${numberText(assumption.radiusMiles)} miles`
  return `Assumed ${numberText(assumption.valueSqm)} m² · median of ${scope} · middle half ${numberText(assumption.lowerQuartileSqm)}–${numberText(assumption.upperQuartileSqm)} m². This is a generic budgeting estimate; an observed measurement is still unavailable. Verify the property's internal area before relying on area-based calculations.`
}
